import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axios from "../../../utils/api-client";
import {
    JobsMatrixState,
    JobsMatrixWorker,
    ManageWorkerJobPayload,
    WorkerJobAuditLog,
} from "./types";
import { JobStatus } from "./types";

const initialState: JobsMatrixState = {
    data: [],
    jobStatuses: [],
    loading: false,
    error: null,
    updateWorkerJobStatus: "idle",
    updateWorkerJobStatusError: null as string | null,
    isJobDetailsDialogOpen: false,
    selectedWorkerAndJob: null,
    filterData: {
        allLocations: [],
        allWorkcenters: [],
        allManagers: [],
        allJobRoles: [],
        workerTypes: [],
        shiftPatterns: [],
        workerStatuses: [],
        workerSubTypes: [],
        filterRelationships: [],
    },
    getJobsMatrixFiltersForUser: {
        status: "idle",
        errorMessage: "",
    },
    defaultFiltersData: null as JobsMatrixState["defaultFiltersData"],
    getJobsMatrixDefaultFilters: {
        status: "idle" as const,
        errorMessage: "",
    },
    fetchWorkerJobAuditLog: {
        status: "idle",
        errorMessage: "",
        data: [],
    },
};

// Fetch Jobs Matrix data
export const fetchJobsMatrixData = createAsyncThunk(
    "jobsMatrix/fetchJobsMatrixData",
    async (_, { fulfillWithValue, rejectWithValue, getState }) => {

        try {
            const getJobsMatrixData = axios.get(
                `${process.env.REACT_APP_API_END_POINT}/JobsMatrix/GetForUser`
            );

            const getJobStatuses = axios.get(
                `${process.env.REACT_APP_API_END_POINT}/WorkerJob/statuses`
            );


            const [jobsMatrixResponse, jobStatusesResponse] =
                await Promise.all([getJobsMatrixData, getJobStatuses]);

            return fulfillWithValue({
                data: jobsMatrixResponse.data as JobsMatrixWorker[],
                jobStatuses: jobStatusesResponse.data as JobStatus[],
            });
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error.message
            );
        }
    }
);

// Fetch audit log for a specific worker and job
export const fetchWorkerJobAuditLog = createAsyncThunk(
    "jobsMatrix/fetchWorkerJobAuditLog",
    async (
        { workerId, jobId }: { workerId: string; jobId: number },
        { fulfillWithValue, rejectWithValue }
    ) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/WorkerJob/audit-log`,
                {
                    params: {
                        WorkerId: workerId,
                        JobId: jobId,
                    },
                }
            );

            return fulfillWithValue(response.data as WorkerJobAuditLog[]);
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error.message
            );
        }
    }
);

// Update worker job status
export const updateWorkerJobStatus = createAsyncThunk(
    "jobsMatrix/updateWorkerJobStatus",
    async (payload: ManageWorkerJobPayload, { fulfillWithValue, rejectWithValue }) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_END_POINT}/WorkerJob/manage`,
                payload
            );

            return fulfillWithValue({ response: response.data, payload });
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error.message
            );
        }
    }
);

// Fetch filters for jobs matrix
export const getJobsMatrixFiltersForUser = createAsyncThunk(
    "jobsMatrix/getJobsMatrixFiltersForUser",
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/SkillsMatrix/GetFiltersV2?includeFilterRelationships=true`
            );

            if (response.status === 200) {
                return fulfillWithValue(response.data);
            } else if (response.status === 204) {
                return fulfillWithValue({});
            }
            return rejectWithValue("An error occurred");
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error.message
            );
        }
    }
);

// Fetch default filters for jobs matrix
export const getJobsMatrixDefaultFilters = createAsyncThunk(
    "jobsMatrix/getJobsMatrixDefaultFilters",
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/SkillsMatrix/GetDefaultFilters`
            );

            if (response.status === 200) {
                return fulfillWithValue(response.data);
            } else if (response.status === 204) {
                return fulfillWithValue(null);
            }
            return rejectWithValue("An error occurred");
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || error.message
            );
        }
    }
);

const jobsMatrixSlice = createSlice({
    name: "jobsMatrix",
    initialState,
    reducers: {
        resetUpdateWorkerJobStatus: (state) => {
            state.updateWorkerJobStatus = "idle";
            state.updateWorkerJobStatusError = null;
        },
        clearAuditLog: (state) => {
            state.fetchWorkerJobAuditLog.data = [];
        },
        setIsJobDetailsDialogOpen: (state, action) => {
            state.isJobDetailsDialogOpen = action.payload;
        },
        setSelectedWorkerAndJob: (state, action) => {
            state.selectedWorkerAndJob = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Jobs Matrix data
            .addCase(fetchJobsMatrixData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJobsMatrixData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.data;
                state.jobStatuses = action.payload.jobStatuses;
            })
            .addCase(fetchJobsMatrixData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetch audit log
            .addCase(fetchWorkerJobAuditLog.pending, (state) => {
                state.fetchWorkerJobAuditLog.status = "pending";
            })
            .addCase(fetchWorkerJobAuditLog.fulfilled, (state, action) => {
                state.fetchWorkerJobAuditLog.status = "fulfilled";
                state.fetchWorkerJobAuditLog.data = action.payload;
            })
            .addCase(fetchWorkerJobAuditLog.rejected, (state, action) => {
                state.fetchWorkerJobAuditLog.status = "error";
                state.fetchWorkerJobAuditLog.errorMessage = action.payload as string;
            })
            // Update worker job status
            .addCase(updateWorkerJobStatus.pending, (state) => {
                state.updateWorkerJobStatus = "pending";
                state.loading = true;
            })
            .addCase(updateWorkerJobStatus.fulfilled, (state, action) => {
                state.updateWorkerJobStatus = "fulfilled";
                state.loading = false;

                // Update the specific worker's job status in the state
                const { payload } = action.payload;
                const workerIndex = state.data.findIndex(
                    (worker) => worker.workerId === payload.workerId
                );

                if (workerIndex !== -1 && payload.jobs.length > 0) {
                    const worker = state.data[workerIndex];
                    const jobUpdate = payload.jobs[0];

                    // Find if the job already exists in the worker's jobs array
                    const jobIndex = worker.jobs?.findIndex(
                        (job) => job.id === jobUpdate.jobId
                    );

                    if (jobIndex !== undefined && jobIndex !== -1 && worker.jobs) {
                        // Update existing job status
                        if (jobUpdate.statusCode === "NOT_QUALIFIED") {
                            // Remove the job from the array
                            worker.jobs.splice(jobIndex, 1);
                        } else {
                            // Update the job status
                            worker.jobs[jobIndex] = {
                                ...worker.jobs[jobIndex],
                                jobStatusCode: jobUpdate.statusCode,
                            };
                        }
                    } else if (jobUpdate.statusCode !== "NOT_QUALIFIED") {
                        // Add new job if it doesn't exist and status is not NOT_QUALIFIED
                        if (!worker.jobs) {
                            worker.jobs = [];
                        }
                        worker.jobs.push({
                            id: jobUpdate.jobId,
                            jobStatusCode: jobUpdate.statusCode,
                            isExpired: false, // Default value, adjust as needed
                        });
                    }
                }
            })
            .addCase(updateWorkerJobStatus.rejected, (state,action) => {
                state.loading = false;
                state.updateWorkerJobStatus = "rejected";
                state.updateWorkerJobStatusError = action.payload as string;
            })
            // Fetch filters for jobs matrix
            .addCase(getJobsMatrixFiltersForUser.pending, (state) => {
                state.getJobsMatrixFiltersForUser.status = "pending";
            })
            .addCase(getJobsMatrixFiltersForUser.fulfilled, (state, action) => {
                try {
                    const {
                        jobs,
                        locations,
                        workcenters,
                        managers,
                        workerTypes,
                        shiftPatterns,
                        workerStatuses,
                        workerSubTypes,
                        filterRelationships,
                    } = action.payload;

                    // Helper function to get location name by ID
                    const getLocationName = (locationId: number) => {
                        const location = locations?.find((loc: any) => loc.id === locationId);
                        return location?.name || "";
                    };

                    state.filterData = {
                        allLocations: locations?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],

                        allWorkcenters: workcenters
                            ?.map((el: any) => ({
                                ...el,
                                nameWithLocation: `${getLocationName(el.locationId)} - ${el.name}`,
                            }))
                            .sort((a: any, b: any) => a.name?.trim().localeCompare(b.name?.trim())) ?? [],

                        allManagers: managers?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],

                        allJobRoles: jobs?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],

                        workerTypes: workerTypes?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],

                        shiftPatterns: shiftPatterns?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],

                        workerStatuses: workerStatuses
                            ?.filter((status: any) => status.code !== "INA")
                            ?.map((status: any) => ({
                                id: status.workerStatusId,
                                statusId: status.workerStatusId,
                                name: status.name,
                                code: status.code,
                            }))
                            .sort((a: any, b: any) => a.name?.trim().localeCompare(b.name?.trim())) ?? [],

                        workerSubTypes: workerSubTypes?.sort((a: any, b: any) =>
                            a.name?.trim().localeCompare(b.name?.trim())
                        ) ?? [],
                        filterRelationships: filterRelationships?.map((rel: any) => ({
                            ...rel,
                            from: rel.from === "workcenters" ? "workCenters" : rel.from,
                        })) ?? [],
                    };

                    state.getJobsMatrixFiltersForUser.status = "fulfilled";
                } catch (e) {
                    state.getJobsMatrixFiltersForUser.status = "error";
                    state.getJobsMatrixFiltersForUser.errorMessage = "An error occurred";
                }
            })
            .addCase(getJobsMatrixFiltersForUser.rejected, (state, action) => {
                state.getJobsMatrixFiltersForUser.status = "error";
                state.getJobsMatrixFiltersForUser.errorMessage =
                    action.payload as string || "An error occurred";
            })
            // Default filters
            .addCase(getJobsMatrixDefaultFilters.pending, (state) => {
                state.getJobsMatrixDefaultFilters.status = "pending";
            })
            .addCase(getJobsMatrixDefaultFilters.fulfilled, (state, action) => {
                state.getJobsMatrixDefaultFilters.status = "fulfilled";
                const data = action.payload;
                state.defaultFiltersData = data
                    ? {
                        ...data,
                        managers: data.managerId ? `${data.managerId}` : (data.managers ?? ""),
                    }
                    : null;
            })
            .addCase(getJobsMatrixDefaultFilters.rejected, (state, action) => {
                state.getJobsMatrixDefaultFilters.status = "error";
                state.getJobsMatrixDefaultFilters.errorMessage =
                    action.payload as string || "An error occurred";
            });
    },
});

export const { resetUpdateWorkerJobStatus, clearAuditLog, setIsJobDetailsDialogOpen, setSelectedWorkerAndJob } =
    jobsMatrixSlice.actions;

export default jobsMatrixSlice.reducer;
